import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Truck, PackageCheck, AlertTriangle, ScrollText, IndianRupee, Wallet, Send, Boxes } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { StatusBadge } from "@/components/StatusBadge";
import { labelOf } from "@/lib/api";

const STATUS_COLORS = ["#0A1128", "#4F46E5", "#D97706", "#059669", "#E11D48", "#EA580C", "#64748B", "#F59E0B"];

function Kpi({ label, value, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{value}</div>
        </div>
        <div className={`p-2 rounded-md bg-${tone}-50 text-${tone}-700`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [dispatches, setDispatches] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, d] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dispatches"),
      ]);
      setData(s.data);
      setDispatches((d.data || []).slice(0, 6));
    })();
  }, []);

  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;

  const statusData = (data.status_distribution || []).map((s) => ({ ...s, name: labelOf(s.status) }));

  return (
    <div data-testid="page-dashboard" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Overview</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Dispatch Command Center</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi testid="kpi-total" label="Total Dispatches" value={data.total} icon={Boxes} tone="slate" />
        <Kpi testid="kpi-transit" label="In Transit" value={data.in_transit} icon={Truck} tone="amber" />
        <Kpi testid="kpi-delivered" label="Delivered" value={data.delivered} icon={PackageCheck} tone="emerald" />
        <Kpi testid="kpi-overdue" label="Overdue" value={data.overdue ?? 0} icon={AlertTriangle} tone="rose" />
        <Kpi testid="kpi-invoice" label="Invoice Value" value={fmtInr(data.invoice_total)} icon={IndianRupee} tone="indigo" />
        <Kpi testid="kpi-transport" label="Transport Cost" value={fmtInr(data.transport_total)} icon={Wallet} tone="orange" />
        <Kpi testid="kpi-pod-pending" label="POD Pending" value={data.pod_pending} icon={ScrollText} tone="yellow" />
        <Kpi testid="kpi-notify-pending" label="Notify Pending" value={data.notify_pending} icon={Send} tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Monthly Dispatch</div>
              <div className="font-heading text-lg font-bold">Invoice vs Transport Cost</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Legend />
              <Bar dataKey="invoice" name="Invoice" fill="#0A1128" radius={[4, 4, 0, 0]} />
              <Bar dataKey="transport" name="Transport" fill="#D97706" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Delivery Status</div>
          <div className="font-heading text-lg font-bold mb-2">Distribution</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie dataKey="count" data={statusData} innerRadius={50} outerRadius={90} paddingAngle={2}>
                {statusData.map((_, i) => (<Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500">Latest</div>
        <div className="font-heading text-lg font-bold mb-3">Recent Dispatches</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
                <th className="py-2">Dispatch</th>
                <th className="py-2">Invoice</th>
                <th className="py-2">LR</th>
                <th className="py-2">Amount</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((d) => (
                <tr key={d.id} className="table-row border-t border-slate-100">
                  <td className="py-2.5 font-mono text-xs">{d.dispatch_code}</td>
                  <td>{d.invoice_number}</td>
                  <td className="font-mono text-xs">{d.lr_number}</td>
                  <td>{fmtInr(d.invoice_amount)}</td>
                  <td><StatusBadge status={d.delivery_status} /></td>
                </tr>
              ))}
              {dispatches.length === 0 && (
                <tr><td colSpan="5" className="py-6 text-center text-slate-500 text-sm">No dispatches yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
